/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';
import { ComposeIcon } from '@coursera/cds-icons';

import { CMLOrHTML } from 'bundles/cml';
import type { QuestionRubricElement, QuestionRubricPrompt } from 'bundles/grading/api/types';
import { SubmissionPartSchemaDetailsCourierTypeNameEnum } from 'bundles/grading/api/types';
import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';
import { AutoSaveContext } from 'bundles/grading/components/staff-graded-assignments/auto-save/utils';
import { InlineFeedbackRubricContext } from 'bundles/grading/components/staff-graded-assignments/form-parts/InlineFeedbackRubricContext';
import type { RubricProps } from 'bundles/grading/components/staff-graded-assignments/form-parts/utils';
import { processContent } from 'bundles/grading/utils/formPartUtils';

import _t from 'i18n!nls/grading';

const styles = {
  prompt: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
};

const InlineFeedbackButton = ({ rubric }: RubricProps) => {
  const { isExpanded, setIsExpanded, isReadOnly } = useContext(InlineFeedbackRubricContext);
  const { submissionData } = useContext(SGAPageContext);
  const { localScopeId } = useContext(AutoSaveContext);

  const { submission } = submissionData.data || {};
  // We need to reset the expanded status
  // - when localScopeId changes to handle situation where submitter is changed and landing on the same rubric, but for a different submitter
  // - when unmounting to ensure revisiting the rubric doesn't trigger opening the box widget by default
  // - when grade visibility is changed, which changes the isReadOnly state in order to switch between view/give inline feedback states
  useEffect(() => {
    setIsExpanded(false);
    return () => {
      setIsExpanded(false);
    };
  }, [localScopeId, setIsExpanded, isReadOnly]);
  const noFileUpload =
    submission?.currentQuestionSubmissionType === SubmissionPartSchemaDetailsCourierTypeNameEnum.FileUploadSchema &&
    !submission?.doesCurrentQuestionSubmissionHaveResponse;

  const prompt = (rubric.formElementNode?.element as QuestionRubricElement)?.prompt;
  const cmlOrHtmlValue = (prompt as QuestionRubricPrompt)?.promptContent
    ? processContent((prompt as QuestionRubricPrompt)?.promptContent)
    : null;

  return (
    <div>
      {cmlOrHtmlValue && (
        <Typography2 component="div" variant="bodyPrimary" css={styles.prompt}>
          <CMLOrHTML value={cmlOrHtmlValue} />
        </Typography2>
      )}
      <Button
        variant="ghost"
        iconPosition="before"
        icon={<ComposeIcon />}
        onClick={() => setIsExpanded(true)}
        disabled={isExpanded || noFileUpload}
      >
        {isReadOnly ? _t('View inline feedback') : _t('Give inline feedback')}
      </Button>
    </div>
  );
};

export default InlineFeedbackButton;
